/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useHistory } from 'react-router-dom';
import { Fragment, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import Table, { TableProps } from 'antd/lib/table';
import { Empty, message, Popover, Tag as TagAntd, Tooltip } from 'antd';
import moment from 'moment';
import { uniq } from 'lodash';
import { queryCache, useMutation } from 'react-query';

// local imports
import { useStyles } from '~src/hooks/useStyles';
import { useFiles } from '~src/hooks/api-hooks/useFiles';
import { useProjectId } from '~src/hooks/useProjectId';
import {
  dbFileToTableEntry,
  sortByNumberProp,
  sortByType,
  TableEntry,
} from './FileTable.utils';
import IconButton from '~components/_common/IconButton/IconButton';
import { VersionSelect } from '~components/_common/VersionSelect/VersionSelect';
import { styleFn } from './FileTable.style';
import { FileTableProps } from './FileTable';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { __DEV__ } from '~src/config';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { SearchContext } from '~src/contexts/SearchContext';
import { filterByQuery } from '~src/utils/searchFilters';
import { toggleVFileNotify } from '~src/services/api';
import { DbFile } from '~src/types/db';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';
import { useSortByRevision } from '~src/hooks/utils/useSortByRevision';
import { formatDate } from '~src/utils/formatDate';
import FilterRecentContext from '~src/contexts/FilterRecentContext';
import { getTagsForFile } from '~src/utils/tags';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { DB_STATE } from '~src/types/enums';
import { ColumnType } from 'antd/es/table/interface';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { getTenderIconArichive } from '~components/molecules/LeftSideView/utils';
import { SVFIconType } from '~components/atoms/Viewers/SVFViewer/components/SVFIconType';

const sortByUpdatedAt = sortByNumberProp('updatedAt');

/** Recent files table */
export const RecentsTable: React.FC<FileTableProps> = ({
  selectedKeys,
  onSelect,
  highlightedRow,
  setSelectedItems,
  handleRow,
  openSVFTranscribe,
  ...tableProps
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const projectId = useProjectId();
  const history = useHistory();
  const { setFileToView } = useFileViewerContext();
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const { filterRecentContext } = useContext(FilterRecentContext);
  const archiveSettings = useArchiveSettings();
  const tagsData = archiveSettings.data?.resTags;
  const isTagsOn = archiveSettings?.data?.resArchiveSettings?.is_tags_on;
  const filesQueryBaseKeys = {
    withVersions: true,
    afterDate: moment(filterRecentContext.afterDate).unix(),
    limit: filterRecentContext.limit,
    localSortBy: null,
    oldFilesImplementation: true,
  };
  const { sortByRevision } = useSortByRevision();

  const filesQuery = useFiles(filesQueryBaseKeys, {
    onError: () => history.replace(`/projects/${projectId}`),
  });

  const [
    doUpdateFileNotifications,
    updateFileNotificationMutation,
  ] = useMutation(toggleVFileNotify, {
    onError: () => {
      message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
    },
    onSuccess: ({ is_notify }, { versionedFileId }) => {
      queryCache.setQueryData<DbFile[]>(
        ['files', { ...filesQueryBaseKeys, projectId: String(projectId) }],
        (prevFiles = []) => {
          const changedFileIndex = prevFiles.findIndex(
            (item) => item.id === versionedFileId
          );
          if (changedFileIndex >= 0) {
            const newList = [...prevFiles];
            newList[changedFileIndex] = {
              ...newList[changedFileIndex],
              is_notify,
            };
            return newList;
          }
          return prevFiles;
        }
      );
    },
  });

  const searchContext = useContext(SearchContext);
  const foldersQuery = useFolders(
    {
      as_admin:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? true
          : undefined,
    },
    { enabled: false }
  );

  const filesSource = useMemo(
    () =>
      (filesQuery.data || []).map((item) => dbFileToTableEntry(item)).reverse(),
    [filesQuery.data]
  );

  const filteredFiles = [
    ...filesSource
      .filter((item) => {
        //getting index of version in [versions] array by checking fileVersionsContext and [all current versions] array
        //Array.prototype.findIndex() || gets the index of the first element in the array that satisfies the provided testing function
        const dynamicIndex = (item.dbFile?.versions || []).findIndex(
          (el) =>
            //provided testing function
            el.version === fileVersionsContext[item.id]?.version &&
            fileVersionsContext[item.id]?.version
        );
        if (
          filterByQuery(item.name, searchContext.query) ||
          filterByQuery(item.description, searchContext.query) ||
          filterByQuery(item.type.icon, searchContext.query) ||
          filterByQuery(
            dynamicIndex > 0
              ? item.dbFile?.versions &&
                  item.dbFile?.versions[dynamicIndex] &&
                  item.dbFile?.versions[dynamicIndex].version
              : item.dbFile?.versions &&
                  item.dbFile?.versions[0] &&
                  item.dbFile?.versions[0].version,
            searchContext.query
          )
        ) {
          return item;
        }
        return null;
      })
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      ),
  ];

  const handleSelectShift = (record: TableEntry, index?: number) => {
    if (selectedKeys && selectedKeys.length === 0) {
      return setSelectedItems((prevSelectedItems) => [
        record,
        ...prevSelectedItems,
      ]);
    }
    if (selectedKeys && selectedKeys.length > 0) {
      const selectedIndex = selectedKeys.findIndex(
        (element) => element === record.key
      );
      if (selectedIndex >= 0) {
        return setSelectedItems((prevItems) =>
          prevItems.filter((item) => item.key !== record.key)
        );
      }
      const tableKeys = getTableData().map((item) => item.key);

      const isAnySelectedInCurrentView = selectedKeys.some((el) =>
        tableKeys.includes(el as string)
      );
      if (!isAnySelectedInCurrentView) {
        return setSelectedItems((prevSelectedItems) => [
          record,
          ...prevSelectedItems,
        ]);
      }

      const lastIndexReversed = getTableData()
        .reverse()
        .findIndex((el) => selectedKeys.includes(el.key));

      const lastIndex = getTableData().findIndex(
        (el) => el.key === getTableData().reverse()[lastIndexReversed].key
      );
      const firstIndex = getTableData().findIndex((el) =>
        selectedKeys.includes(el.key)
      );

      if ((index as number) >= 0) {
        if ((index as number) < firstIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(index, firstIndex),
          ]);
        }

        if ((index as number) > lastIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(lastIndex + 1, (index as number) + 1),
          ]);
        }

        if ((index as number) > firstIndex && (index as number) < lastIndex) {
          return setSelectedItems((prevState) =>
            uniq([
              ...prevState,
              ...getTableData().slice(firstIndex, (index as number) + 1),
            ])
          );
        }
      }
    }
  };

  const getTableData = () => {
    return [...filteredFiles];
  };

  const columns = useMemo<TableProps<TableEntry>['columns']>(
    () => [
      {
        title: '',
        width: 30,
        align: 'center',
        dataIndex: 'type',
        render: (type, record) => {
          return {
            children: (
              <a
                className="ant-dropdown-link"
                onClick={(e) => {
                  e.preventDefault();
                  const customEvent = new MouseEvent('contextmenu', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    button: 2,
                    buttons: 2,
                    clientX: e.clientX,
                    clientY: e.clientY,
                  });
                  e.currentTarget.dispatchEvent(customEvent);
                }}
                css={styles.dotAnchor}>
                <IconButton
                  iconStyle="fal"
                  icon="ellipsis-v"
                  customStyle={styles.dotIcon}
                  color={'darkGray'}
                  tooltip={intl.formatMessage({ id: 'actions' })}
                />
              </a>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 60,
        align: 'center',
        render: (type, record) => {
          const isTenderFolder = record.dbFolder?.option?.is_tender_folder;
          const isPreviewableFormat =
            type.icon === 'file-pdf' || type.icon === 'file-image';

          return {
            children: (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {isTenderFolder ? (
                    <NavIcon
                      customStyle={{ marginLeft: '32px' }}
                      icon={getTenderIconArichive(record.dbFolder?.option)}
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {/* 1) Eye PDF/IMG */}
                      {isPreviewableFormat && (
                        <IconButton
                          click={() => {
                            if (record.dbFile?.versions?.length) {
                              setFileToView({ ...record.dbFile });
                            } else {
                              message.error(
                                intl.formatMessage({ id: 'file-preview-error' })
                              );
                            }
                          }}
                          iconStyle={type.iconStyle}
                          icon={['far', 'eye']}
                          color="red"
                        />
                      )}

                      {/* 2) SVF: transcribe/view */}
                      {(() => {
                        const version = fileVersionsContext[record.id];
                        const hasModel = version?.extension_svf_model != null;
                        const isConvertible =
                          version?.is_svf_convertible === true;
                        const isViewable =
                          version?.extension_svf_model?.state === 7;
                        const shouldShow = isConvertible || isViewable;
                        if (!shouldShow) return null;

                        return (
                          <SVFIconType
                            key={`svf-${record.id}-${
                              version?.extension_svf_model?.state ?? 'none'
                            }`}
                            version={version}
                            extension={record.extension}
                            variant="sidebar"
                            disabled={false}
                            customTooltip={(() => {
                              const state = version?.extension_svf_model?.state;
                              if (!hasModel && isConvertible)
                                return intl.formatMessage({
                                  id: 'svf-view-file-convert',
                                });
                              if ([1, 2, 4].includes(state as number))
                                return intl.formatMessage({
                                  id: 'svf-view-file-progress',
                                });
                              if (state === 7)
                                return intl.formatMessage({
                                  id: 'svf-view-file-window',
                                });
                              if (state === 5)
                                return intl.formatMessage({
                                  id: 'svf-view-file-convert',
                                });
                              return intl.formatMessage({
                                id: 'svf-view-file',
                              });
                            })()}
                            onClick={() => {
                              if (!record.dbFile) {
                                message.error(
                                  intl.formatMessage({
                                    id: 'file-preview-error',
                                  })
                                );
                                return;
                              }
                              const v = fileVersionsContext[record.id];
                              const ready = v?.extension_svf_model?.state === 7;

                              if (ready) {
                                setFileToView({ ...record.dbFile });
                                return;
                              }
                              if (
                                openSVFTranscribe &&
                                (isConvertible ||
                                  v?.extension_svf_model?.state === 5)
                              ) {
                                openSVFTranscribe({ ...record.dbFile });
                                setFileToView(undefined);
                              }
                            }}
                            type={type}
                          />
                        );
                      })()}

                      {/* 3) file/CAD-icon */}
                      {(() => {
                        const ext = record.extension?.toLowerCase() || '';
                        const isCadFile = ['rvt', 'dwg', 'ifc'].includes(ext);

                        if (isCadFile) {
                          return (
                            <SVFIconType
                              key={`file-icon-${record.id}`}
                              version={fileVersionsContext[record.id]}
                              extension={record.extension}
                              variant="table"
                              customTooltip={intl.formatMessage(
                                { id: type.icon },
                                { type: record.extension?.toUpperCase() }
                              )}
                              onClick={() => {
                                //
                              }}
                              type={type}
                            />
                          );
                        }

                        return (
                          <IconButton
                            iconStyle={type.iconStyle}
                            icon={type.icon}
                            iconSecond={
                              record.state === DB_STATE.DELETED
                                ? ['fad', 'trash']
                                : null
                            }
                            colorSecondIcon="red"
                            customStyleSecondIcon={{
                              left: '12px',
                              top: '10px',
                            }}
                            color={type.color}
                            customStyle={styles.icons}
                            tooltip={intl.formatMessage(
                              { id: type.icon },
                              { type: record.extension?.toUpperCase() }
                            )}
                            badge={
                              record.state !== DB_STATE.DELETED &&
                              type.is_empty &&
                              '0'
                            }
                            backgroundOff
                          />
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            ),
          };
        },
        sorter: sortByType,
      },
      {
        title: intl.formatMessage({ id: 'name' }),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (name, record) => (
          <Fragment>
            {name}
            {record.favourite && (
              <IconButton icon={['fas', 'star']} color="yellow" backgroundOff />
            )}
          </Fragment>
        ),
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'description',
        sorter: (a, b) =>
          (a.description || '').localeCompare(b.description || ''),
        width: 200,
        ellipsis: true,
      },
      {
        title: intl.formatMessage({ id: 'revision' }),
        dataIndex: 'dbFile',
        sorter: (a, b) => sortByRevision(a, b),
        width: 200,
        ellipsis: true,
        render: (x, record) => {
          if (!record.dbFile?.versions) return null; // only for files
          return (
            fileVersionsContext[record.id]?.version ||
            record.dbFile.versions[0]?.version
          );
        },
      },
      {
        title: intl.formatMessage({ id: 'tags' }),
        dataIndex: 'tags',
        width: 215,
        render: (tag, record) => {
          const tagsArray = record?.dbFile?.tags || [];
          const tagsDataActive = tagsData?.filter(
            (tag) => tag.state !== DB_STATE.REMOVED
          );
          const tags = getTagsForFile(tagsArray, tagsDataActive);

          if (!record.dbFile || !tags) {
            return null;
          }

          const maxVisibleTags = 2;
          const hiddenCount =
            tags.length > maxVisibleTags ? tags.length - maxVisibleTags : 0;

          return {
            children: (
              <Fragment>
                {tags.slice(0, maxVisibleTags).map((tagItem, index) => {
                  const tag = String(tagItem);
                  return (
                    <TagAntd key={index} color={'#1f5fa6'}>
                      <Tooltip title={tag}>
                        {tag.length > 8 ? `${tag.substring(0, 8)}...` : tag}
                      </Tooltip>
                    </TagAntd>
                  );
                })}
                {hiddenCount > 0 && (
                  <Popover
                    overlayStyle={{ minWidth: 100, maxWidth: 250 }}
                    content={
                      <div
                        css={{
                          padding: '10px',
                        }}>
                        <h3>
                          <b>{intl.formatMessage({ id: 'tags' })}</b>
                        </h3>
                        {tags.map((tag, index) => (
                          <TagAntd
                            style={{ marginBottom: '4px' }}
                            key={index}
                            color={'#1f5fa6'}>
                            {tag}
                          </TagAntd>
                        ))}
                      </div>
                    }>
                    <TagAntd>+{hiddenCount}</TagAntd>
                  </Popover>
                )}
              </Fragment>
            ),
          };
        },
      },
      {
        title: (
          <IconButton
            containerStyle={{ minHeight: '18px' }}
            icon={['far', 'envelope']}
            backgroundOff
          />
        ),
        dataIndex: 'notifications',
        width: 60,
        align: 'center',
        render: (text, element, index) => (
          <IconButton
            icon={
              element.notifications
                ? ['fad', 'check-circle']
                : ['far', 'circle']
            }
            color={element.notifications ? 'green' : 'darkGray'}
            customStyle={[styles.icons, styles.small]}
            backgroundOff
            click={async () => {
              doUpdateFileNotifications({
                projectId: Number(projectId),
                versionedFileId: element.id,
                notify: !element.notifications,
              });
            }}
          />
        ),
      },
      {
        title: intl.formatMessage({ id: 'ver' }),
        dataIndex: 'dbFile',
        width: 80,
        align: 'center',
        onCell: () => ({ style: { padding: '4px 10px' } }),
        render: (file) => {
          if (!file) return null;
          return <VersionSelect file={file} />;
        },
      },
      {
        title: intl.formatMessage({ id: 'last-updated' }),
        dataIndex: 'updatedAt',
        sorter: sortByUpdatedAt,
        width: 145,
        align: 'center',
        render: (updatedAt) => formatDate(updatedAt, 'short'),
      },
    ],
    [fileVersionsContext, intl, styles.icons, styles.small, tagsData]
  );

  const filteredColumns = (columns as ColumnType<TableEntry>[]).filter(
    (column) => {
      return column?.dataIndex !== 'tags';
    }
  );

  const updatedColumns = !isTagsOn ? filteredColumns : columns;

  return (
    <Table<TableEntry>
      showSorterTooltip={false}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={intl.formatMessage({ id: 'no-data' })}
          />
        ),
      }}
      css={[
        styles.table,
        tableProps.scroll?.y && {
          '.ant-table-body': {
            height: tableProps.scroll.y,
          },
        },
      ]}
      className="main-content-window"
      pagination={false}
      bordered
      columns={updatedColumns}
      dataSource={[...filteredFiles]}
      loading={
        filesQuery.isLoading ||
        filesQuery.isIdle ||
        foldersQuery.isLoading ||
        foldersQuery.isIdle
      }
      rowClassName={(record) => {
        let className = '';
        if (record.key === highlightedRow?.key) {
          className += 'ant-table-row-selected' + ' ';
        }
        if (record.disabled) {
          className += 'unfinished' + ' ';
        }

        return className;
      }}
      rowSelection={{
        type: 'checkbox',
        selectedRowKeys: selectedKeys,
        columnWidth: 50,
        hideSelectAll: true,
        onSelect,
        getCheckboxProps: (record) => ({
          disabled: record.disabled,
        }),
      }}
      onRow={(record, index) => ({
        ...handleRow(record, index),
        onClick: (event) => {
          if (event.shiftKey) {
            return handleSelectShift(record, index);
          }
          const onClickAction = handleRow(record, index).onClick;
          if (onClickAction) {
            return onClickAction(event);
          }
        },
      })}
      {...tableProps}
    />
  );
};
